'use client'
import React, { useState, useEffect } from 'react'
import { FaLinkedinIn, FaFacebook } from "react-icons/fa"
import { PiLinkSimpleBold } from "react-icons/pi"
import { FaXTwitter } from "react-icons/fa6"
import { useTranslations } from 'next-intl'

interface TocItem {
  id: string;
  text: string;
  level: number;
  children: Array<{
    id: string;
    text: string;
    level: number;
  }>;
}

interface ArticleClientInteractionsProps {
  toc: TocItem[];
}

export default function ArticleClientInteractions({ 
  toc
}: ArticleClientInteractionsProps) {
  const t = useTranslations('articles')
  
  // State for Share Copy Alert
  const [copySuccess, setCopySuccess] = useState(false)
  // State to track active section in TOC
  const [activeSection, setActiveSection] = useState('')

  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  // Track scroll position to highlight current section in TOC
  useEffect(() => {
    const handleScroll = () => {
      const headings = Array.from(document.querySelectorAll('h2[id], h3[id]'))
      
      // Find the heading that's currently in view
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i] as HTMLElement
        const rect = heading.getBoundingClientRect()
        
        // If the heading is in view (with some offset for the sticky header)
        if (rect.top <= 150) {
          setActiveSection(heading.id)
          break
        }
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleShareX = () => {
    window.open(`https://x.com/intent/tweet?url=${encodeURIComponent(currentUrl)}`, '_blank')
  }

  const handleShareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`, '_blank')
  }

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`, '_blank')
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch {
      alert("Failed to copy the link")
    }
  }

  // Scroll smoothly to the target element
  const handleTOCClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    // Update the URL hash without triggering the default jump
    window.history.pushState(null, '', `#${id}`)

    // Use document.getElementById to search the entire DOM for the heading
    const element = document.getElementById(id)
    if (element) {
      // Calculate target scroll position with an offset for fixed headers
      const offset = 100
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset
      window.scrollTo({ top: y, behavior: 'smooth' })
      
      // Update active section
      setActiveSection(id)
    }
  }

  // This component handles the left sidebar with TOC and sharing buttons
  return (
    <div className="w-full md:w-1/3 md:self-start">
      <div className="sticky top-[100px] z-10">
        {/* Table of Contents */}
        <div className="text-sm bg-surface/50 p-4 rounded-lg border border-border/10 max-h-[calc(100vh-120px)] overflow-y-auto">
          <h2 className="font-bold mb-4 text-primary text-lg">
            {t('toc.title')}
          </h2>
          <ul className="space-y-2">
            {toc.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`hover:text-primary transition-colors ${
                    activeSection === item.id ? 'text-primary font-medium' : 'text-text-main'
                  }`}
                  onClick={(e) => handleTOCClick(e, item.id)}
                >
                  {item.text}
                </a>
                {item.children && item.children.length > 0 && (
                  <ul className="ml-4 mt-1 space-y-1">
                    {item.children.map((child) => (
                      <li key={child.id}>
                        <a
                          href={`#${child.id}`}
                          className={`hover:text-primary transition-colors ${
                            activeSection === child.id ? 'text-primary font-medium' : 'text-text-main'
                          }`}
                          onClick={(e) => handleTOCClick(e, child.id)}
                        >
                          {child.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Share Buttons */}
        <div className="mt-6">
          <div className="flex space-x-4">
            <button
              onClick={handleShareX}
              className="p-3 rounded-full border border-border/20 hover:bg-surface transition text-text-main"
              aria-label={t('share.buttons.twitter')}
            >
              <FaXTwitter className="w-[16px] h-[16px]" />
            </button>
            <button
              onClick={handleShareLinkedIn}
              className="p-3 rounded-full border border-border/20 hover:bg-surface transition text-text-main"
              aria-label={t('share.buttons.linkedin')}
            >
              <FaLinkedinIn className="w-[16px] h-[16px]" />
            </button>
            <button
              onClick={handleShareFacebook}
              className="p-3 rounded-full border border-border/20 hover:bg-surface transition text-text-main"
              aria-label={t('share.buttons.facebook')}
            >
              <FaFacebook className="w-[16px] h-[16px]" />
            </button>
            <button
              onClick={handleCopyLink}
              className="p-3 rounded-full border border-border/20 hover:bg-surface transition text-text-main"
              aria-label={t('share.buttons.copyLink')}
            >
              <PiLinkSimpleBold className="w-[16px] h-[16px]" />
            </button>
          </div>
          {copySuccess && (
            <div className="mt-2 text-status-success text-sm">
              {t('share.copySuccess')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 